/* ============================================================================
 * Viori — Shared Attribution Module
 *
 * Boots on EVERY storefront page (loaded sync from layout/theme.liquid, ahead
 * of personalization-state.js) so first-touch is recorded on the real landing
 * page. The problem it solves: a visitor arrives from an ad carrying UTMs,
 * browses five pages, and only then opens the shop quiz. The params have to
 * survive that walk, and they have to survive it without a cookie.
 *
 * Two records are kept under one storage key ('viori_attr'):
 *   first_touch — written once on the first page of the visit, never overwritten
 *   last_touch  — overwritten on any later page load that carries a param
 *
 * Each record also carries landing_page (pathname+search), referrer and ts.
 *
 * Storage mirrors the three-tier pattern in personalization-state.js:
 *   localStorage (only with consent) → sessionStorage → in-memory.
 * No cookie is written.
 *
 * Public API — window.VioriAttribution:
 *   snapshot()        → flat object ready to spread into a Klaviyo properties
 *                       payload. Empty values are omitted; never empty strings.
 *   snapshotUpper()   → the same object keyed UPPERCASE_SNAKE (UTM_SOURCE,
 *                       GCLID, FIRST_TOUCH_UTM_SOURCE, LANDING_PAGE, …) for
 *                       Brevo, whose contact attributes are uppercase.
 *   getFirstTouch()   → copy of the first-touch record (or null)
 *   getLastTouch()    → copy of the last-touch record (or null)
 *   getFlag(name)     → read a string flag from the same storage tier
 *   setFlag(name,val) → write a string flag to the same storage tier
 *
 * getFlag/setFlag exist so other modules (the V6 shop quiz's
 * 'viori_quiz_captured' marker) land in the SAME tier as the attribution
 * record instead of re-implementing the consent/fallback ladder and drifting.
 * ========================================================================== */

(function (root) {
  'use strict';

  if (root.VioriAttribution) return;

  var LS_KEY      = 'viori_attr';
  var FLAG_PREFIX = 'viori_flag_';
  var CONSENT_KEY = 'viori_consent';

  // Order here is the order they appear in snapshot().
  var PARAM_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'utm_id', 'utm_persona', 'gclid', 'fbclid', 'ttclid', 'msclkid', 'irclickid'
  ];

  // ── Storage tiers ─────────────────────────────────────────────────────────
  // Same shape as personalization-state.js: probe localStorage once, fall back
  // to sessionStorage (survives navigation, dies with the tab), then to an
  // in-memory bucket shared with the rest of the Viori modules.
  if (!root.__vioriMem) root.__vioriMem = {};
  var _lsAvail = null;

  function _lsOk() {
    if (_lsAvail !== null) return _lsAvail;
    try {
      localStorage.setItem('__v_attr_probe__', '1');
      localStorage.removeItem('__v_attr_probe__');
      _lsAvail = true;
    } catch (e) {
      _lsAvail = false;
    }
    return _lsAvail;
  }

  function _lsGet(key) {
    try { return _lsOk() ? localStorage.getItem(key) : null; } catch (e) { return null; }
  }
  function _lsSet(key, val) {
    try { if (_lsOk()) { localStorage.setItem(key, val); return true; } } catch (e) { _lsAvail = false; }
    return false;
  }
  function _ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function _ssSet(key, val) {
    try { sessionStorage.setItem(key, val); return true; } catch (e) { return false; }
  }

  // ── Consent ───────────────────────────────────────────────────────────────
  // VioriState is the authority when it is on the page, but it is loaded
  // per-section (PDP, quiz, collection header) rather than site-wide, so on
  // most templates it is simply absent. When it is, read the same two sources
  // it reads internally: the persisted viori_consent flag, then Shopify's
  // customerPrivacy API. Default is no consent.
  function hasConsent() {
    try {
      if (root.VioriState && typeof root.VioriState.hasConsent === 'function') {
        return !!root.VioriState.hasConsent();
      }
    } catch (e) { /* fall through */ }

    var stored = _lsGet(CONSENT_KEY);
    if (stored === 'true')  return true;
    if (stored === 'false') return false;

    try {
      if (root.Shopify && root.Shopify.customerPrivacy &&
          typeof root.Shopify.customerPrivacy.userCanBeTracked === 'function') {
        return !!root.Shopify.customerPrivacy.userCanBeTracked();
      }
    } catch (e) { /* ignore */ }

    return false;
  }

  // ── Record read / write ───────────────────────────────────────────────────
  // Read walks every tier regardless of the current consent state: a visitor
  // who lands without consent writes to sessionStorage, then accepts the
  // banner mid-visit. Walking the tiers lets the next write promote that
  // record into localStorage instead of stranding it.
  function _readRaw() {
    var raw = null;
    if (hasConsent()) raw = _lsGet(LS_KEY);
    if (!raw) raw = _ssGet(LS_KEY);
    if (!raw) raw = root.__vioriMem[LS_KEY] || null;
    return raw;
  }

  function _read() {
    var raw = _readRaw();
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function _write(rec) {
    var json;
    try { json = JSON.stringify(rec); } catch (e) { return false; }
    if (hasConsent() && _lsSet(LS_KEY, json)) return true;
    if (_ssSet(LS_KEY, json)) return true;
    root.__vioriMem[LS_KEY] = json;
    return true;
  }

  // ── Capture ───────────────────────────────────────────────────────────────
  function readParams() {
    var found = {};
    try {
      var p = new URLSearchParams(root.location.search);
      for (var i = 0; i < PARAM_KEYS.length; i++) {
        var k = PARAM_KEYS[i];
        var v = p.get(k);
        if (v) {
          v = String(v).trim();
          if (v) found[k] = v;
        }
      }
    } catch (e) { /* URLSearchParams unavailable — no params captured */ }
    return found;
  }

  function buildTouch(params) {
    var t = {};
    for (var i = 0; i < PARAM_KEYS.length; i++) {
      var k = PARAM_KEYS[i];
      if (params[k]) t[k] = params[k];
    }
    try {
      t.landing_page = root.location.pathname + root.location.search;
    } catch (e) { t.landing_page = ''; }
    try {
      t.referrer = document.referrer || '';
    } catch (e) { t.referrer = ''; }
    t.ts = new Date().toISOString();
    return t;
  }

  function capture() {
    var params  = readParams();
    var hasAny  = false;
    for (var k in params) { if (params.hasOwnProperty(k)) { hasAny = true; break; } }

    var rec = _read();

    if (!rec || !rec.first_touch) {
      // True landing page of the visit — recorded whether or not it carries
      // params, so landing_page/referrer are always known. last_touch is
      // seeded from the same touch and diverges as soon as a param shows up.
      var touch = buildTouch(params);
      rec = rec || {};
      rec.first_touch = touch;
      rec.last_touch  = rec.last_touch || touch;
      _write(rec);
      return rec;
    }

    if (hasAny) {
      rec.last_touch = buildTouch(params);
      _write(rec);
    }
    return rec;
  }

  // ── Public API ────────────────────────────────────────────────────────────
  function put(out, key, val) {
    if (val === null || val === undefined) return;
    var s = String(val);
    if (!s) return;               // never send empty strings
    out[key] = s;
  }

  function snapshot() {
    var rec   = _read() || {};
    var first = rec.first_touch || {};
    // Falling back to first_touch keeps landing_page/referrer populated for a
    // visit that never carried a param.
    var last  = rec.last_touch || rec.first_touch || {};

    var out = {};
    for (var i = 0; i < PARAM_KEYS.length; i++) {
      put(out, PARAM_KEYS[i], last[PARAM_KEYS[i]]);
    }
    put(out, 'first_touch_utm_source',   first.utm_source);
    put(out, 'first_touch_utm_medium',   first.utm_medium);
    put(out, 'first_touch_utm_campaign', first.utm_campaign);
    put(out, 'first_touch_landing_page', first.landing_page);
    put(out, 'landing_page', last.landing_page);
    put(out, 'referrer',     last.referrer);

    // Session id is VioriState's to mint. Null when that module is not on the
    // page rather than a second, competing id.
    var sid = null;
    try {
      if (root.VioriState && typeof root.VioriState.getSessionId === 'function') {
        sid = root.VioriState.getSessionId() || null;
      }
    } catch (e) { /* ignore */ }
    out.viori_session_id = sid;

    return out;
  }

  // Brevo keys its contact attributes in UPPERCASE_SNAKE, so the same flat
  // record needs a second casing rather than a second source of truth. Derived
  // from snapshot() so the two can never drift: add a key there and it appears
  // here automatically. Empty values are already dropped by put(); the extra
  // guard below catches viori_session_id, which snapshot() sets to null (not
  // omitted) when VioriState is absent.
  function snapshotUpper() {
    var src = snapshot();
    var out = {};
    for (var k in src) {
      if (!src.hasOwnProperty(k)) continue;
      var v = src[k];
      if (v === null || v === undefined) continue;
      v = String(v);
      if (!v) continue;
      out[k.toUpperCase()] = v;
    }
    return out;
  }

  function copyOf(o) {
    if (!o) return null;
    var c = {};
    for (var k in o) { if (o.hasOwnProperty(k)) c[k] = o[k]; }
    return c;
  }

  function getFirstTouch() { var r = _read(); return r ? copyOf(r.first_touch) : null; }
  function getLastTouch()  { var r = _read(); return r ? copyOf(r.last_touch)  : null; }

  // ── Shared flags ──────────────────────────────────────────────────────────
  function getFlag(name) {
    if (!name) return null;
    var key = FLAG_PREFIX + name;
    var v = null;
    if (hasConsent()) v = _lsGet(key);
    if (v === null) v = _ssGet(key);
    if (v === null) v = (root.__vioriMem[key] === undefined ? null : root.__vioriMem[key]);
    return v;
  }

  function setFlag(name, val) {
    if (!name) return false;
    var key = FLAG_PREFIX + name;
    var s = String(val);
    if (hasConsent() && _lsSet(key, s)) return true;
    if (_ssSet(key, s)) return true;
    root.__vioriMem[key] = s;
    return true;
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  capture();

  // Consent usually arrives after the landing page (banner interaction), so
  // re-persist once it flips — promoting the record from sessionStorage into
  // localStorage where it survives the tab closing.
  try {
    document.addEventListener('viori:consent_changed', function () {
      var rec = _read();
      if (rec) _write(rec);
    });
  } catch (e) { /* ignore */ }

  root.VioriAttribution = {
    snapshot: snapshot,
    snapshotUpper: snapshotUpper,
    getFirstTouch: getFirstTouch,
    getLastTouch: getLastTouch,
    getFlag: getFlag,
    setFlag: setFlag
  };

}(window));
